import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { KeycloakService } from 'keycloak-angular';
import { from, of, switchMap, catchError } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Adds the Keycloak bearer token to every API call, plus the standard JSON
 * headers.
 *
 * NOTE on `enableBearerInterceptor: true` in KeycloakInitService: that flag
 * enables keycloak-angular's OWN bearer interceptor, which is a class-based
 * HTTP_INTERCEPTORS provider registered by KeycloakAngularModule. This app
 * bootstraps standalone with `provideHttpClient(withInterceptors([...]))` and
 * never imports that module, so the built-in interceptor never runs — which is
 * why the token has to be attached here.
 */

function originOf(url: string): string {
  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return '';
  }
}


const API_ORIGINS: ReadonlySet<string> = new Set(
  [
    window.location.origin,
    environment.URL,
    (environment as Record<string, unknown>)['SELF_SERVICE_CHAT_API_URL'] as string,
    (environment as Record<string, unknown>)['agentApiUrl'] as string,
  ]
    .filter((value): value is string => typeof value === 'string' && !!value.trim())
    .map(originOf)
    .filter(Boolean),
);

/** Keycloak's own endpoints authenticate themselves — never forward our token. */
const KEYCLOAK_BASE = String(environment.KEYCLOAK_URL ?? '').replace(/\/+$/, '');
const BEARER_TOKEN_ENABLED =
  environment.bearer_token === true ||
  String(environment.bearer_token).toLowerCase() === 'true';

function shouldAttachToken(req: HttpRequest<unknown>): boolean {
  if (!BEARER_TOKEN_ENABLED) return false;
  if (KEYCLOAK_BASE && req.url.startsWith(KEYCLOAK_BASE)) return false;
  const origin = originOf(req.url);
  return !!origin && API_ORIGINS.has(origin);
}

export const authHeadersInterceptor: HttpInterceptorFn = (req, next) => {
  const keycloak = inject(KeycloakService);

  const isFormData = typeof FormData !== 'undefined' && req.body instanceof FormData;
  const baseHeaders: Record<string, string> = { Accept: 'application/json' };
  if (!isFormData) {
    // Let the browser set the multipart boundary for FormData uploads.
    baseHeaders['Content-Type'] = 'application/json';
  }

  const send = (token?: string) =>
    next(
      req.clone({
        setHeaders: token ? { ...baseHeaders, Authorization: `Bearer ${token}` } : baseHeaders,
      }),
    );

  if (!shouldAttachToken(req) || !keycloak.getKeycloakInstance) {
    return send();
  }

  let loggedIn = false;
  try {
    loggedIn = keycloak.isLoggedIn();
  } catch {
    loggedIn = false;
  }
  if (!loggedIn) return send();

  return from(keycloak.updateToken(30)).pipe(
    catchError(() => of(false)),
    switchMap(() => send(keycloak.getKeycloakInstance()?.token)),
  );
};
